import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, Polygon, CircleMarker, useMap, useMapEvents } from 'react-leaflet';
import { DivIcon, Control, DomUtil, DomEvent } from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useLocation } from '../router/RouterContext';
import { ForecastLocation } from '../api/types';
import { useMapContext } from '../contexts/MapContext';
import { useI18n } from '../i18n/I18nContext';
import { storageService, MapViewState } from '../services/StorageService';
import { UserLocation } from '../services/GeolocationService';
import { useGeolocation } from '../hooks/useGeolocation';
import { Flash } from './Flash';
import 'leaflet/dist/leaflet.css';

interface MapPageProps {
  onLogout: () => void;
}


// Current location marker icon (Google Maps style)
const currentLocationIcon = new DivIcon({
  html: `<div class="current-location-dot"></div>`,
  className: 'current-location-marker',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const DEFAULT_MAP_STATE: MapViewState = {
  center: [42.17, 12.59], // Center on Italy
  zoom: 6,
  minZoom: 6,
  maxZoom: 12,
};

/* Mappa di sfondo.
 * - 'classica': rilievo del tile server (con token dalla funzione getTileServerToken) + OSM trasparente.
 * - 'topo': OpenTopoMap, la stessa del sito nuovo; non serve il token.
 * Dalla 3.5.0 il default e' 'topo'; in compilazione REACT_APP_MAPPA=classica torna alla vecchia.
 * Per confrontarle sulla stessa installazione: ?mappa=topo o ?mappa=classica, ricordato nel browser. */
type Mappa = 'classica' | 'topo';
const MAPPA: Mappa = (() => {
  const ok = (v: string | null | undefined): v is Mappa => v === 'classica' || v === 'topo';
  try {
    const q = new URLSearchParams(window.location.search).get('mappa');
    if (ok(q)) { localStorage.setItem('mappa_sfondo', q); return q; }
    const m = localStorage.getItem('mappa_sfondo');
    if (ok(m)) return m;
  } catch (e) { /* storage non disponibile */ }
  return ok(process.env.REACT_APP_MAPPA) ? process.env.REACT_APP_MAPPA : 'topo';
})();

// Map boundaries based on actual forecast locations (Italy + nearby countries)
// Extra buffer so outermost spots appear in center when panning to edges
const MAP_BOUNDS: [[number, number], [number, number]] = [
  [34.4516, 3.3907], // Southwest corner (2 degrees extra buffer)
  [49.9639, 19.7863]  // Northeast corner (2 degrees extra buffer)
];

// Load initial map state before component renders to avoid race conditions
const INITIAL_MAP_STATE = storageService.getMapViewState() || DEFAULT_MAP_STATE;

// Custom Leaflet control for location button
interface LocationControlOptions {
  onLocationClick: () => void;
  isLocating?: boolean;
  locationText?: string;
  locationAlt?: string;
}
//
class LocationControl extends Control {
  private onLocationClick: () => void;
  private isLocating: boolean = false;
  private button!: HTMLAnchorElement;
  private locationText: string;
  private locationAlt: string;

  constructor(options: LocationControlOptions) {
    super({ position: 'topleft' });
    this.onLocationClick = options.onLocationClick;
    this.isLocating = options.isLocating || false;
    this.locationText = options.locationText || 'Go to my location';
    this.locationAlt = options.locationAlt || 'Location';
  }

  onAdd() {
    const container = DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
    this.button = DomUtil.create('a', '', container);
    this.button.href = '#';
    this.button.title = this.locationText;
    this.updateIcon();
    this.button.style.width = '30px';
    this.button.style.height = '30px';
    this.button.style.display = 'flex';
    this.button.style.alignItems = 'center';
    this.button.style.justifyContent = 'center';
    this.button.style.textDecoration = 'none';

    DomEvent.on(this.button, 'click', DomEvent.stopPropagation)
      .on(this.button, 'click', DomEvent.preventDefault)
      .on(this.button, 'click', this.onLocationClick);

    return container;
  }

  updateLoadingState(isLocating: boolean) {
    this.isLocating = isLocating;
    this.updateIcon();
  }

  private updateIcon() {
    if (!this.button) return;

    const animationClass = this.isLocating ? 'location-pulse' : '';
    this.button.className = animationClass;
    this.button.innerHTML = `
      <img src="/location_icon.svg" alt="${this.locationAlt}" width="18" height="18" style="display: block; margin: auto;" />
    `;
  }
}

// Component to handle map interactions
// Module-level flag to track if map is ready (persists across remounts)
let isMapReadyFlag = false;

const MapController: React.FC<{
  userLocation: UserLocation | null;
  onMapStateChange: (state: MapViewState) => void;
  onLocationRequest: () => void;
  isLocating: boolean;
  onMapReady?: (map: any) => void;
  locationText: string;
  locationAlt: string;
}> = ({ userLocation, onMapStateChange, onLocationRequest, isLocating, onMapReady, locationText, locationAlt }) => {
  const map = useMap(); //

  useEffect(() => {
    // Only set up whenReady callback if not already ready
    if (!isMapReadyFlag) {
      map.whenReady(() => {
        isMapReadyFlag = true;
      });
    }

    // Always call onMapReady if provided
    if (onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  
  useEffect(() => {
    if (userLocation) {
      map.setView([userLocation.lat, userLocation.lng], 12);
    }
  }, [map, userLocation]);

  useEffect(() => {
    const handleMapChange = () => {
      // Only save state after map is ready
      if (!isMapReadyFlag) {
        return;
      }

      const zoom = map.getZoom();
      const center = map.getCenter();
      const newState: MapViewState = {
        center: [center.lat, center.lng],
        zoom: zoom,
        minZoom: map.getMinZoom(),
        maxZoom: map.getMaxZoom()
      };

      onMapStateChange(newState as Omit<MapViewState, 'timestamp'>);
    };

    map.on('zoomend', handleMapChange);
    map.on('moveend', handleMapChange);

    return () => {
      map.off('zoomend', handleMapChange);
      map.off('moveend', handleMapChange);
    };
  }, [map, onMapStateChange]);

  useEffect(() => {
    const locationControl = new LocationControl({
      onLocationClick: onLocationRequest,
      isLocating: isLocating,
      locationText: locationText,
      locationAlt: locationAlt
    });
    map.addControl(locationControl);

    return () => {
      map.removeControl(locationControl);
    };
  }, [map, onLocationRequest, isLocating, locationText, locationAlt]);
  
  return null;
};

/**
 * Pressione lunga (o tasto destro) sulla mappa: windgram di quel punto. Leaflet emette
 * 'contextmenu' in entrambi i casi, come sul sito nuovo. Il tocco breve sulla mappa toglie
 * la cella disegnata dall'ultimo windgram.
 */
const MapLongPress: React.FC<{ onLongPress: (lat: number, lng: number) => void; onTap: () => void }> = ({ onLongPress, onTap }) => {
  useMapEvents({
    contextmenu: (e) => {
      // senza, comparirebbe anche il menu del browser (o "salva immagine" sulle tile)
      if (e.originalEvent && e.originalEvent.preventDefault) e.originalEvent.preventDefault();
      onLongPress(e.latlng.lat, e.latlng.lng);
    },
    click: () => onTap(),
  });
  return null;
};

export const MapPage: React.FC<MapPageProps> = ({ onLogout }) => {
  const { t } = useI18n(); //
  const [, setLocation] = useLocation();
  const { regions, setMapInstance, error, isLoading, retryLoad, selectedCell, setSelectedCell } = useMapContext();
  const { userLocation, isLocating, locationError, getCurrentLocation } = useGeolocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.menu-container')) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMenuOpen]);

  // Fetch the session token for the hillshade layer
  useEffect(() => {
    if (MAPPA !== 'classica') return;
    const fetchToken = async () => {
      try {
        // IMPORTANT: Replace with your actual Cloud Function URL
        const response = await fetch('https://us-central1-rasp-map.cloudfunctions.net/getTileServerToken');
        if (!response.ok) {
          throw new Error('Failed to fetch tile server token');
        }
        const data = await response.json();
        setSessionToken(data.token);
      } catch (err: any) {
        console.error("Error fetching tile server token:", err);
        setTokenError(t('map.tokenError') as string);
      }
    };

    fetchToken();
  }, [t]);

  const handleMapStateChange = useCallback((newState: Omit<MapViewState, 'timestamp'>) => {
    // Just save to storage service, don't store in React state to avoid re-renders
    storageService.setMapViewState(newState);
  }, []);


  const handleLongPress = useCallback((lat: number, lng: number) => {
    setSelectedCell({ point: [lat, lng], cell: null });
    setLocation('/punto/:lat/:lon', { params: { lat: lat.toFixed(5), lon: lng.toFixed(5) } });
  }, [setLocation, setSelectedCell]);

  const handleMapTap = useCallback(() => setSelectedCell(null), [setSelectedCell]);

  const handleMapReady = useCallback((map: any) => {
    setMapInstance(map);
  }, [setMapInstance]);

  const allLocations = useMemo((): ForecastLocation[] => {
    return regions.flatMap(region => region.windgram_list);
  }, [regions]);

  const memoizedMarkers = useMemo(() =>
    allLocations.map((location) => {
      const iconSize = 32; // Windsock icon size - this defines the clickable area
//
      // Center the icon
      const anchorX = iconSize / 2;
      const anchorY = iconSize / 2;

      const markerIcon = new DivIcon({
        html: `<div class="windsock-marker">
          <div class="windsock-highlight"></div>
          <img src="/windsock_icon.svg" alt="Windsock" class="windsock-icon" />
        </div>`,
        className: 'windsock-container',
        iconSize: [iconSize, iconSize],
        iconAnchor: [anchorX, anchorY],
        popupAnchor: [0, -anchorY],
      });

      return (
        <Marker
          key={location.windgram_id}
          position={[parseFloat(location.coord.lat), parseFloat(location.coord.lng)]}
          icon={markerIcon}
          eventHandlers={{
            click: () => setLocation('/spot/:id', { params: { id: location.windgram_id } })
          }}
        >
          <Tooltip
            permanent
            direction="top"
            offset={[0, -16]}
            className="marker-tooltip"
          >
            {location.windgram_name}
          </Tooltip>
        </Marker>
      );
    }), [allLocations, setLocation]
  );

  // Always render the map, show status overlays as needed
  return (
    <div className="map-page">
      {/* Scenario 1: Loading with no data - show loading spinner overlay */}
      {isLoading && regions.length === 0 && (
        <Flash message={t('map.loading')} variant="normal" />
      )}

      {/* Scenario 2: Load failed with no data - show error with retry overlay */}
      {error && regions.length === 0 && !isLoading && (
        <Flash message={error} onRetry={retryLoad} variant="error" />
      )}

      {/* Location error (geolocation) */}
      {locationError && <Flash message={locationError} variant="error" />}

      {/* Token error */}
      {tokenError && <Flash message={tokenError} variant="error" />}

      {/* Scenarios 3 & 4: We have data (either from cache or successful load)
          If there's an error but we have cached data, we silently ignore it */}

      <div className="map-container">
        <MapContainer
          center={INITIAL_MAP_STATE.center}
          zoom={Math.max(DEFAULT_MAP_STATE.minZoom, Math.min(INITIAL_MAP_STATE.zoom, DEFAULT_MAP_STATE.maxZoom))}
          minZoom={DEFAULT_MAP_STATE.minZoom}
          maxZoom={DEFAULT_MAP_STATE.maxZoom}
          maxBounds={MAP_BOUNDS}
          maxBoundsViscosity={1.0}
          style={{ height: '100%', width: '100%' }}
        >
          <MapController
            userLocation={userLocation}
            onMapStateChange={handleMapStateChange}
            onLocationRequest={getCurrentLocation}
            isLocating={isLocating}
            onMapReady={handleMapReady}
            locationText={t('ui.location')}
            locationAlt={t('ui.location')}
          />
          {MAPPA === 'topo' && (
            <TileLayer
              attribution='Dati &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, SRTM | Stile: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA)'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              maxZoom={17}
            />
          )}
          {MAPPA === 'classica' && sessionToken && (
            <TileLayer
              attribution="Copernicus DEM"
              url={`https://srv1170391.hstgr.cloud/data/hillshade/{z}/{x}/{y}.png?token=${sessionToken}`}
              minZoom={6}
              maxZoom={12}
              zIndex={1}
            />
          )}
          {MAPPA === 'classica' && (
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              opacity={0.65}
              zIndex={2}
            />
          )}
          
          <MapLongPress onLongPress={handleLongPress} onTap={handleMapTap} />

          {/* il punto premuto e, appena arriva la risposta, la cella del modello usata */}
          {selectedCell && (
            <CircleMarker
              center={selectedCell.point}
              radius={4}
              pathOptions={{ color: '#d00', weight: 2, fillColor: '#d00', fillOpacity: 0.9 }}
              interactive={false}
            />
          )}
          {selectedCell && selectedCell.cell && (
            <Polygon
              positions={selectedCell.cell}
              pathOptions={{ color: '#d00', weight: 2, opacity: 0.8, fillColor: '#d00', fillOpacity: 0.12 }}
              interactive={false}
            />
          )}

          {userLocation && (
            <Marker
              position={[userLocation.lat, userLocation.lng]}
              icon={currentLocationIcon}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent.stopPropagation();
                }
              }}
            />
          )}
          
          <MarkerClusterGroup
            chunkedLoading
            animate={false}
            animateAddingMarkers={false}
            disableClusteringAtZoom={15}
            maxClusterRadius={50}
            spiderfyOnMaxZoom={false}
            iconCreateFunction={(cluster: any) => {
              return new DivIcon({
                html: `<div class="cluster-icon">
                  <span>${cluster.getChildCount()}</span>
                </div>`,
                className: 'custom-marker-cluster',
                iconSize: [40, 40],
                iconAnchor: [20, 20],
              });
            }}
          >
            {memoizedMarkers}
          </MarkerClusterGroup>
        </MapContainer>

        <button onClick={() => setLocation('/favourites')} className="favourites-button-overlay" title={t('map.favourites')}>
          <img src="/heart_filled_icon.svg" alt={t('map.favourites')} width="20" height="20" />
        </button>

        <button onClick={() => setLocation('/search')} className="search-button-overlay" title={t('map.search')}>
          <img src="/search_icon.svg" alt={t('map.search')} width="20" height="20" />
        </button>

        <div className="menu-container">
          <button onClick={() => setIsMenuOpen(prev => !prev)} className="menu-button-overlay" title={t('map.menu')}>
            <img src="/menu_icon.svg" alt={t('map.menu')} width="20" height="20" />
          </button>

          {isMenuOpen && (
            <div className="menu-dropdown">
              <button onClick={() => { setLocation('/user'); setIsMenuOpen(false); }} className="menu-item">
                <img src="/user_icon.svg" alt={t('account.title')} width="18" height="18" />
                {t('account.title')}
              </button>
              <button onClick={() => { setLocation('/about'); setIsMenuOpen(false); }} className="menu-item">
                <img src="/info_icon.svg" alt={t('about.title')} width="18" height="18" />
                {t('about.title')}
              </button>
              <button onClick={() => { setLocation('/logout'); setIsMenuOpen(false); }} className="menu-item">
                <img src="/logout_icon.svg" alt={t('map.logout')} width="18" height="18" />
                {t('map.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};